import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import SealBadge from '../components/SealBadge';
import ScoreBreakdown from '../components/ScoreBreakdown';
import FeedbackCard from '../components/FeedbackCard';
import { RecommendationsManager } from '../components/RecommendationsSection';
import { AlertCircle, Calendar, TrendingUp, Info, Award, Shield, CheckCircle, Clock, ArrowLeft, Pencil } from 'lucide-react';
import { useTranslation } from '../translations';
import { useAuth } from '../App';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPEC_OPTIONS = [
  { value: '', label: 'Não informado' },
  { value: 'sangrador', label: 'Sangrador' },
  { value: 'gerente', label: 'Gerente de Campo' },
  { value: 'agronomo', label: 'Agrônomo' },
  { value: 'operador', label: 'Operador de Máquinas' },
  { value: 'outros', label: 'Outros' },
];

const QUAL_LABELS = {
  engenheiro_agronomo: 'Engenheiro Agrônomo',
  tecnico_agricola: 'Técnico Agrícola',
  lider_equipe: 'Líder de Equipe',
  proprietario: 'Proprietário',
  outro: 'Outro',
};

const PLAN_LABELS = {
  emp_gratuito: 'Gratuito', emp_basico: 'Básico', emp_essencial: 'Essencial', emp_completo: 'Completo',
  prof_gratuito: 'Gratuito', prof_plus: 'Plus',
};

/* Formulário de edição dos próprios dados */
const MyDataForm = ({ user, isProfessional, onSaved }) => {
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    city: user?.city || '',
    specialization: user?.specialization || '',
    experience_years: user?.experience_years ?? '',
  });
  const [saving, setSaving] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { name: form.name, phone: form.phone, city: form.city };
      if (isProfessional) {
        payload.specialization = form.specialization;
        payload.experience_years = form.experience_years === '' ? 0 : Number(form.experience_years);
      }
      await axios.put(`${API}/me/profile`, payload);
      toast.success('Dados atualizados com sucesso!');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar os dados');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="my-data-section">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-gray-800">
        <Pencil className="text-green-600 w-6 h-6" />
        Meus Dados
      </h2>
      <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo *</label>
          <input
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            required
            data-testid="profile-edit-name"
            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Telefone (zap, com DDD)</label>
          <input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            placeholder="(17) 99999-0000"
            data-testid="profile-edit-phone"
            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Cidade</label>
          <input
            value={form.city}
            onChange={(e) => setForm({ ...form, city: e.target.value })}
            placeholder="Ex: Barretos - SP"
            data-testid="profile-edit-city"
            className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
          />
        </div>
        {isProfessional && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Especialização</label>
              <select
                value={form.specialization}
                onChange={(e) => setForm({ ...form, specialization: e.target.value })}
                data-testid="profile-edit-specialization"
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:outline-none bg-white"
              >
                {SPEC_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Anos de experiência</label>
              <input
                type="number"
                min="0"
                max="70"
                value={form.experience_years}
                onChange={(e) => setForm({ ...form, experience_years: e.target.value })}
                data-testid="profile-edit-experience"
                className="w-full p-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-green-500 focus:outline-none"
              />
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <button
            type="submit"
            disabled={saving}
            data-testid="profile-edit-save"
            className="w-full sm:w-auto px-6 py-2.5 rounded-lg bg-green-700 text-white font-semibold hover:bg-green-800 disabled:opacity-60"
          >
            {saving ? 'Salvando...' : '💾 Salvar dados'}
          </button>
          {isProfessional && (
            <p className="text-xs text-gray-500 mt-2">
              📱 Com o telefone preenchido, proprietários conseguem falar com você também fora do chat.
            </p>
          )}
        </div>
      </form>
    </div>
  );
};

/* Visão de conta para proprietário/admin */
const AccountView = ({ user, refreshUser }) => {
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    axios.get(`${API}/my-subscription`).then((res) => setPlan(res.data)).catch(() => {});
  }, []);

  const typeLabel = user.user_type === 'admin' ? 'Administrador' : 'Proprietário / Empregador';
  const planLabel = PLAN_LABELS[plan?.plan_id] || plan?.plan_id || '—';

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="account-profile-page">
      <div className="container mx-auto px-4 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-green-700 font-medium mb-4 hover:underline" data-testid="profile-back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar ao painel
        </Link>

        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-1">Minha Conta</h1>
          <p className="text-gray-600">{typeLabel}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-6">
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500">E-mail</p>
              <p className="font-medium text-gray-800 break-all">{user.email}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500">Plano atual</p>
              <p className="font-medium text-gray-800">
                💎 {planLabel}
                {plan?.status === 'trial' && <span className="text-xs text-amber-600 ml-2">(período de teste)</span>}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500">Qualificação técnica</p>
              <p className="font-medium text-gray-800">{QUAL_LABELS[user.qualificacao_tecnica] || 'Não informada'}</p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg border">
              <p className="text-xs text-gray-500">Membro desde</p>
              <p className="font-medium text-gray-800">
                {user.created_at ? new Date(user.created_at).toLocaleDateString('pt-BR') : '—'}
              </p>
            </div>
          </div>
        </div>

        <MyDataForm user={user} isProfessional={false} onSaved={refreshUser} />
      </div>
    </div>
  );
};

const WorkerDashboard = () => {
  const { workerId } = useParams();
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const [seal, setSeal] = useState(null);
  const [latestEvaluation, setLatestEvaluation] = useState(null);
  const [certificationStatus, setCertificationStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const isOwn = user?.id === workerId;
  const isOwnEmployer = isOwn && (user?.user_type === 'employer' || user?.user_type === 'admin');

  useEffect(() => {
    fetchWorkerData();
    // eslint-disable-next-line
  }, [workerId, user?.id]);

  const fetchWorkerData = async () => {
    try {
      setLoading(true);
      setError(null);

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('token');
      const authHeaders = token ? { Authorization: `Bearer ${token}` } : {};

      // Selo atual (público)
      const sealResponse = await fetch(`${backendUrl}/api/evaluations/users/${workerId}/current-seal`);
      if (sealResponse.ok) {
        setSeal(await sealResponse.json());
      }

      // Última avaliação — apenas o próprio usuário logado (requer Bearer)
      if (token && user?.id === workerId) {
        const historyResponse = await fetch(
          `${backendUrl}/api/evaluations/users/${workerId}/history?limit=1`,
          { headers: authHeaders }
        );
        if (historyResponse.ok) {
          const historyData = await historyResponse.json();
          if (historyData.length > 0) {
            const evalResponse = await fetch(
              `${backendUrl}/api/evaluations/${historyData[0].id}`,
              { headers: authHeaders }
            );
            if (evalResponse.ok) {
              setLatestEvaluation(await evalResponse.json());
            }
          }
        }

        // Status de certificação (LGPD) — só do próprio
        try {
          const certResponse = await fetch(`${backendUrl}/api/certification/status?target_user_id=${workerId}`, {
            headers: authHeaders,
          });
          if (certResponse.ok) {
            setCertificationStatus(await certResponse.json());
          }
        } catch {
          console.log('Status de certificação não disponível');
        }
      }
    } catch (err) {
      console.error('Erro ao buscar dados:', err);
      setError(t('wdErrorLoadData'));
    } finally {
      setLoading(false);
    }
  };

  // Proprietário/admin vendo o próprio perfil → visão de conta
  if (isOwnEmployer) {
    return <AccountView user={user} refreshUser={refreshUser} />;
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto p-6 max-w-4xl">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 flex items-start gap-3">
          <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
          <div>
            <h3 className="text-red-900 font-semibold mb-1">{t('wdError')}</h3>
            <p className="text-red-700">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  // Calcular pontuações por grupo - COMPATÍVEL COM v1 E v2
  const calculateGroupScores = (criteria) => {
    if (!criteria) return { A: 0, B1: 0, B2: 0, C: 0, total: 0 };

    const isV2 = criteria.aptitude_evaluation !== undefined ||
                 criteria.panel_opening !== undefined ||
                 criteria.coagulation_control !== undefined;

    if (isV2) {
      return {
        A: (criteria.angle_cutting || 0) + (criteria.cutting_depth || 0) +
           (criteria.bark_consumption || 0) + (criteria.generatrix_marking || 0),
        B1: (criteria.aptitude_evaluation || 0) + (criteria.flagging || 0) +
            (criteria.equipment_installation || 0) + (criteria.panel_opening || 0),
        B2: (criteria.injury_prevention || 0) + (criteria.tapping_recovery || 0) +
            (criteria.gutter_cleaning || 0),
        C: (criteria.ethrel_application || 0) + (criteria.fungicide_application || 0) +
           (criteria.coagulation_control || 0),
        version: 'v2'
      };
    } else {
      return {
        A: (criteria.angle_cutting || 0) + (criteria.cutting_depth || 0) +
           (criteria.bark_consumption || 0) + (criteria.generatrix_marking || 0),
        B1: 0,
        B2: (criteria.gutter_cleaning || 0) + (criteria.tapping_recovery || 0) +
            (criteria.injury_prevention || 0),
        C: (criteria.ethrel_application || 0) + (criteria.fungicide_application || 0) +
           (criteria.coagulation || 0),
        D: criteria.relative_productivity || null,
        version: 'v1'
      };
    }
  };

  // Confidence label helper
  const getConfidenceInfo = (level, count) => {
    if (level === 'high' || count >= 5) return { label: 'Alta', color: 'text-green-700 bg-green-50 border-green-200' };
    if (level === 'medium' || count >= 3) return { label: 'Media', color: 'text-blue-700 bg-blue-50 border-blue-200' };
    return { label: 'Inicial', color: 'text-amber-700 bg-amber-50 border-amber-200' };
  };

  const evalCount = seal?.evaluation_count || 0;
  const confidence = getConfidenceInfo(seal?.confidence_level, evalCount);

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="worker-dashboard">
      <div className="container mx-auto px-4 max-w-6xl">
        <Link to="/" className="inline-flex items-center gap-2 text-green-700 font-medium mb-4 hover:underline" data-testid="profile-back-btn">
          <ArrowLeft className="w-4 h-4" /> Voltar ao painel
        </Link>

        {/* Header com Selo Profissional */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <h1 className="text-4xl font-bold mb-2 text-gray-800">
                {isOwn ? 'Meu Perfil Profissional' : 'Perfil do Profissional'}
              </h1>
              <p className="text-gray-600 flex items-center gap-2 justify-center md:justify-start">
                <Award className="w-5 h-5" />
                Selo Profissional HeveaTalent
              </p>
            </div>
            <div>
              <SealBadge
                sealType={seal?.seal_type || 'none'}
                size="lg"
                evaluationCount={evalCount}
              />
            </div>
          </div>

          {/* Informações do Selo Acumulativo */}
          {seal && seal.seal_type !== 'none' && (
            <div className="mt-6" data-testid="seal-cumulative-info">
              <div className="mb-3 text-center md:text-left">
                <span className="inline-flex items-center gap-1.5 text-xs bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full border border-blue-200">
                  {seal.seal_category === 'combined'
                    ? '50% Tecnica + 50% Contextual'
                    : seal.seal_category === 'contextual'
                    ? '100% Desempenho Contextual'
                    : '100% Avaliacao Tecnica (14 criterios)'}
                </span>
              </div>

              <div className="p-4 rounded-lg border bg-green-50 border-green-200">
                <div className="flex flex-col sm:flex-row items-start gap-4">
                  <div className="flex-1">
                    <p className="text-green-800 font-semibold text-lg flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Selo Acumulativo Ativo
                    </p>
                    <p className="text-green-700 text-sm mt-1">
                      Pontuacao media: <strong>{seal.total_score?.toFixed(1) || '0'}/100</strong>
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <div className="text-center px-4 py-2 bg-white rounded-lg border border-green-200" data-testid="evaluation-count">
                      <p className="text-2xl font-bold text-green-700">{evalCount}</p>
                      <p className="text-xs text-green-600">{evalCount === 1 ? 'avaliacao' : 'avaliacoes'}</p>
                    </div>
                    <div className={`text-center px-4 py-2 rounded-lg border ${confidence.color}`} data-testid="confidence-level">
                      <p className="text-sm font-bold">Confianca</p>
                      <p className="text-xs">{confidence.label}</p>
                    </div>
                  </div>
                </div>
                <p className="text-green-600 text-xs mt-3">
                  {isOwn ? 'Seu selo evolui a cada nova avaliacao. Mais avaliacoes = maior confianca.' : 'O selo evolui a cada nova avaliacao. Mais avaliacoes = maior confianca.'}
                </p>
              </div>
            </div>
          )}

          {/* Sem Avaliação */}
          {seal && seal.seal_type === 'none' && (
            <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-1" />
                <div>
                  <p className="text-blue-800 font-semibold mb-1">
                    {isOwn ? 'Voce ainda nao possui selo profissional' : 'Este profissional ainda nao possui selo'}
                  </p>
                  <p className="text-blue-700 text-sm">
                    {isOwn
                      ? 'Entre em contato com seu gestor para agendar sua primeira avaliacao tecnica.'
                      : 'O selo e conquistado por meio de avaliacoes tecnicas na plataforma.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Meus Dados — edição (apenas o próprio) */}
        {isOwn && <MyDataForm user={user} isProfessional={true} onSaved={refreshUser} />}

        {/* Pontuação Atual (apenas o próprio) */}
        {isOwn && latestEvaluation && (
          <>
            <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
              <h2 className="text-2xl font-bold mb-6 flex items-center gap-2 text-gray-800">
                <TrendingUp className="text-blue-600 w-7 h-7" />
                Sua Pontuacao
              </h2>

              <ScoreBreakdown
                groupScores={calculateGroupScores(latestEvaluation.criteria)}
                totalScore={latestEvaluation.total_score}
              />

              <div className="mt-6 pt-6 border-t border-gray-200">
                <p className="text-sm text-gray-600 flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Ultima avaliacao: {new Date(latestEvaluation.evaluation_date).toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: 'long',
                    year: 'numeric'
                  })}
                </p>
              </div>
            </div>

            {latestEvaluation.feedback && (
              <FeedbackCard feedback={latestEvaluation.feedback} />
            )}
          </>
        )}

        {/* Consentimento LGPD (apenas o próprio) */}
        {isOwn && (
          <div className="bg-white rounded-lg shadow-lg p-6 mb-6" data-testid="lgpd-section">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2 text-gray-800">
              <Shield className="text-green-600 w-7 h-7" />
              Consentimento LGPD
            </h2>

            {certificationStatus?.certification_status === 'active' ? (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-8 h-8 text-green-600" />
                  <div>
                    <p className="text-green-800 font-semibold text-lg">Consentimento ativo</p>
                    <p className="text-green-700 text-sm">
                      Seus dados estao protegidos conforme a LGPD. Seu perfil esta visivel para empregadores.
                    </p>
                    {certificationStatus.opted_in_date && (
                      <p className="text-green-600 text-sm mt-1">
                        Aceito em: {new Date(certificationStatus.opted_in_date).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ) : certificationStatus?.certification_status === 'pending_consent' ? (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Clock className="w-8 h-8 text-yellow-600" />
                  <div className="flex-1">
                    <p className="text-yellow-800 font-semibold text-lg">Pendente</p>
                    <p className="text-yellow-700 text-sm mb-3">
                      Aceite o termo LGPD para tornar seu perfil visivel para empregadores.
                    </p>
                    <Link
                      to="/certification"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-lg hover:bg-yellow-700 transition-colors"
                      data-testid="complete-lgpd-btn"
                    >
                      <Shield className="w-4 h-4" />
                      Aceitar Termo LGPD
                    </Link>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-3">
                  <Info className="w-8 h-8 text-blue-600" />
                  <div className="flex-1">
                    <p className="text-blue-800 font-semibold text-lg">Aceite necessario</p>
                    <p className="text-blue-700 text-sm mb-3">
                      Para que seu selo profissional seja visivel publicamente, aceite o termo LGPD.
                      Seus dados serao protegidos conforme a lei.
                    </p>
                    <Link
                      to="/certification"
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                      data-testid="accept-lgpd-btn"
                    >
                      <Shield className="w-4 h-4" />
                      Aceitar Termo LGPD
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Minhas Recomendações (apenas o próprio) */}
        {isOwn && (
          <div className="mb-6">
            <RecommendationsManager userId={workerId} />
          </div>
        )}

        {/* Informação sobre Sistema */}
        <div className="mt-8 p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
          <h3 className="font-semibold text-purple-900 mb-2">
            Sobre o Selo Profissional
          </h3>
          <p className="text-sm text-purple-800 mb-2">
            O sistema HeveaTalent avalia 14 criterios tecnicos baseados em literatura tecnica oficial (INCAPER, CATI/SP).
            O selo e acumulativo e evolui com cada nova avaliacao.
          </p>
          <ul className="text-sm text-purple-700 space-y-1 ml-4">
            <li>{t('wdBronze')}</li>
            <li>{t('wdSilver')}</li>
            <li>{t('wdGold')}</li>
          </ul>
          <p className="text-xs text-purple-600 mt-3">
            {t('wdTripod')}: <strong>{t('purposeJustice')}</strong> / <strong>{t('purposeSecurity')}</strong> / <strong>{t('purposeSimplicity')}</strong>
          </p>
        </div>
      </div>
    </div>
  );
};

export default WorkerDashboard;
